import {
  ArrayInput,
  Button,
  SimpleFormIterator,
  TextInput,
  required,
  useNotify,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { Box, alpha } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";
import { employeeRequests } from "api/employeeApi";
import { NODE_LEAVE_FILES_LIMIT } from "utils/constants";
import FileInputWithlabel from "../components/FileInputWithLabel";

const iteratorSx = {
  "& .RaSimpleFormIterator-list": {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
    gap: 2,
  },
  "& .RaSimpleFormIterator-line": {
    m: 0,
    p: 2,
    alignItems: "flex-start",
  },
  "& .RaSimpleFormIterator-form": {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  // Make the upload area read as a drop target instead of a flat grey band.
  "& .RaFileInput-dropZone": {
    border: theme => `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
    borderRadius: "5px",
    backgroundColor: "#FFF",
    cursor: "pointer",
    transition: "background-color .15s, border-color .15s",
    "&:hover": {
      borderColor: theme => theme.palette.primary.main,
      backgroundColor: theme => alpha(theme.palette.primary.main, 0.06),
    },
  },
};

const AttachFileRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const documentValue = useWatch({ name: `documents.${index}.document` });
  const description = useWatch({ name: `documents.${index}.description` });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <FileInputWithlabel isEnableValidate={!!description} />
      <TextInput
        source="description"
        label="Име на файла"
        fullWidth
        validate={documentValue ? required() : undefined}
        // Keeps the name field aligned at the bottom of both cards even when one of
        // them grew taller after a file preview was rendered.
        sx={{ mt: "auto", mb: 0 }}
      />
    </Box>
  );
};

const DocumentsIterator = () => {
  const documents = useWatch({ name: "documents" });

  return (
    <ArrayInput
      source="documents"
      label={false}
    >
      <SimpleFormIterator
        disableAdd={documents?.length >= NODE_LEAVE_FILES_LIMIT}
        disableReordering
        disableClear
        sx={iteratorSx}
      >
        <AttachFileRow />
      </SimpleFormIterator>
    </ArrayInput>
  );
};

const AttachFilesDialog = ({ leaveId, iconOnly = false }) => {
  const notify = useNotify();

  if (!leaveId) return null;

  const handleConfirm = async ({ documents }) => {
    // react-admin's FileInput stores { rawFile, src, title } — the File itself is `rawFile`.
    const rows = (documents ?? []).filter(row => row?.document?.rawFile instanceof File);

    if (!rows.length) {
      notify("Прикачете поне един файл.", { type: "warning" });
      return false;
    }
    if (rows.some(row => !row.description?.trim())) {
      // required() accepts a whitespace-only value, so guard here as well.
      notify("Въведете име за всеки прикачен файл.", { type: "warning" });
      return false;
    }

    await dataProvider.customRequest(
      employeeRequests.uploadLeaveDocumentAttachments(leaveId),
      {
        id: leaveId,
        attachments: rows.map(row => ({
          fileName: row.description.trim(),
          file: row.document.rawFile,
        })),
      },
      "POST",
      null,
      true, // multipart
    );

    notify(rows.length === 1 ? "Файлът е прикачен успешно." : `Успешно прикачени файла: ${rows.length}.`, {
      type: "success",
    });
  };

  return (
    <FormModal
      titleContent={`Прикачване на файлове към отпуск №${leaveId}`}
      dialogSize="md"
      saveTitle="Прикачи"
      disableBackdropClose
      defaultValues={{ documents: [{}] }}
      onConfirm={handleConfirm}
      triggerButton={
        <Button label={iconOnly ? "" : "Прикачи файлове"}>
          <AttachFileIcon />
        </Button>
      }
    >
      <DocumentsIterator />
    </FormModal>
  );
};

export default AttachFilesDialog;
